
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import { Tooltip } from '@mui/material';
import '../../../stylesheet.css';
import { useLocation } from 'react-router-dom';

function PurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const branchId = 0;
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {

        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('PurchaseApi', `/PurchaseDetails/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/PurchaseCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();

        }
        else if (location.state?.from === '/PurchaseEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }

        else if (location.state?.from === '/PurchaseDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
            // handleClickOpen(false);
        }
    }, [location]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-5px" }}>
            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px" }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Purchase
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

                {/* From Date */}
                <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* To Date */}
                <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* Refresh Button */}
                <button
                    onClick={fetchtableData}
                    style={{
                        background: "#1090a3",
                        color: "white",
                        fontSize: "12px",
                        border: "none",
                        borderRadius: "20px",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}
                >
                    Refresh
                </button>
                {hasCreateMasterPermission && (
                    <Tooltip title="Create New Purchase" arrow>
                        <Button
                            component={Link}
                            to="/PurchaseCreate"
                            style={{
                                background: "#3F4F44",
                                color: "white",
                                fontSize: "12px",
                                border: "none",
                                borderRadius: "20px",
                                padding: "4px 14px",
                                cursor: "pointer",
                                transition: "0.2s",
                                fontFamily: 'inherit'
                            }}
                        >
                            Create
                        </Button>
                    </Tooltip>
                )}
            </div>

            <div
                className="rounded-lg overflow-hidden shadow-sm"
                style={{ maxHeight: "480px", overflowY: "auto", marginTop: '-10px' }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#0f172a" }}> {/* Dark navy-blue */}
                                {[
                                    "Sl.No",
                                    "Bill Date",
                                    "Bill No",
                                    "Supplier",
                                    "Amount",
                                    "CGST",
                                    "SGST",
                                    "IGST",
                                    "Total",
                                    "Qty",
                                    "",
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            borderBottom: "2px solid #1e293b",
                                            color: "#f8fafc", // soft white
                                            padding: "10px 12px",
                                            textAlign: ["Amount", "CGST", "SGST", "IGST", "Total", "Qty"].includes(head)
                                                ? "right"
                                                : "left",
                                            letterSpacing: "0.5px",
                                            backgroundColor: "#0f172a", // Dark navy-blue
                                            padding: '2px'

                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody sx={{ backgroundColor: '#F1EFEC' }}>
                            {filteredData.map((item, index) =>(
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'white', // Change color on hover
                                        },
                                        cursor: 'pointer',
                                    }}
                                >   <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{index + 1}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{item.BillNumber}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px' }}>{item.SupplierName}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.Amount}</TableCell>
                                    <TableCell className='table-header-cell.child-align-right' style={{ padding: '3px', textAlign: 'right' }}>{item.CGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child-align-right' style={{ padding: '3px', textAlign: 'right' }}>{item.SGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child-align-right' style={{ padding: '3px', textAlign: 'right' }}>{item.IGSTAmount}</TableCell>
                                    <TableCell className='table-header-cell.child-align-right' style={{ padding: '3px', textAlign: 'right' }}>{item.TotalAmount}</TableCell>
                                    <TableCell className='table-header-cell.child' style={{ padding: '3px', textAlign: 'right' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell className='table-header-cell.child-align-right' style={{ padding: '3px', textAlign: 'right' }}>

                                        <Tooltip title="Purchase Details" arrow>
                                            <IconButton component={Link} to={`/PurchaseDetails/${item.Id}`} color="warning">
                                                <Info style={{ fontSize: '19px' }} />
                                            </IconButton>
                                        </Tooltip>

                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip
                                                title="Purchase Return" arrow>
                                                <IconButton component={Link} to={`/PurchaseReturnCreate/${item.Id}`} color="primary">
                                                    <ApiIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip title="Edit Purchase" arrow>
                                                <IconButton component={Link} to={`/PurchaseEdit/${item.Id}`} color="primary">
                                                    <EditIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {hasDeleteMasterPermission && item.IsActive && (

                                            <Tooltip title="Delete Purchase" arrow>
                                                <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                    <DeleteIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {!item.IsActive && (
                                            <Tooltip title="Activate Purchase" arrow>
                                                <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                    <ActivateIcon style={{ fontSize: '19px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Confirmation Dialog */}

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>

            </Dialog>

        </div >
    );
}

export default PurchaseIndex;



